import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Card,
  Select,
  Typography,
  Box,
  FormHelperText,
  Stack,
  InputAdornment,
} from '@mui/material';
import MDButton from 'components/MDButton';
import { useDispatch, useSelector } from 'react-redux';
import { openSnackbar } from '../../../redux/slices/common';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { memberValidation } from '../../../validations/member';
import { isUnder21 } from 'utilities/common';
import { isEmptyObject } from 'utilities/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { DatePicker } from '@mui/x-date-pickers';
import CalendarIcon from 'common/customIcons/calendar';
import { AccountCircle } from '@mui/icons-material';
import InputMask from 'react-input-mask';
import GoogleSuggestion from 'components/googleMaps/GoogleSuggestion';
import { updateMember } from '../../../redux/slices/hyperwolf/members';

const AddMemberForm = () => {
  const { memberDetails, loading = false } = useSelector(state => state.hyperwolfMemberSlice);
  const { memberDetail = [] } = memberDetails || [];
  const { isUploading = false } = useSelector(state => state.common);
  const { user } = useSelector(state => state.auth);
  const addressError = useRef(false);

  const { memberEmail } = useParams();
  const [formData, setFormData] = useState({
    dob: '',
    email: '',
    primaryPhone: '',
    password: '',
    firstName: '',
    lastName: '',
    licenseNumber: '',
    sex: '',
    idImage: '',
    createdBy: user?.name || '',
    status: 'active',
    city: '',
    state: '',
    address: '',
    consumerType: '',
    recId: '',
    memberGroup: { name: '' },
    userType: '',
  });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const banMemberSubmitHandler = useCallback((banMemberPayload) => {    
    const payload = {
      dob: dayjs(formData?.dob).valueOf(),
      email: formData.email,
      primaryPhone: formData?.primaryPhone?.replace(/[^\d]/g, ''),
      firstName: formData.firstName,
      lastName: formData.lastName,
      licenseNumber: formData?.licenseNumber,
      gender: formData?.gender,
      deliveryAddress: {
        address: formData?.address,
        city: formData?.city,
        state: formData?.state,
        zipcode: formData?.zipcode,
        latitude: formData?.lat,
        longitude: formData?.lng,
        apartment: formData?.apartment,
      },
      idImage: formData?.idImage,
      createdBy: formData?.createdBy,
      recId: formData?.recId,
      consumerType: formData?.consumerType,
      memberGroup: formData?.memberGroup,
      verifyMethod: 'admin',
      memberNotesEmployee: formData?.createdBy,
      notesTime: dayjs().valueOf(),
      ...banMemberPayload
    };
    
    dispatch(updateMember({ data:payload, email:memberEmail })).unwrap().then((res) => {
      showError(res?.message, 'success');
      dispatch(closeCustomModalDialog({}));
      navigate('/hyperwolf/members');
    }).catch((err) => {
      showError(err?.message, 'error');
    });
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (addressError.current) {
      return;
    }
    if (!memberEmail) {
      const { errors, isValid } = memberValidation(formData, isMedicnalOr21Less);
      if (!isValid) {
        setErrors(errors);
        return;
      }
    }
    setErrors({});

    let payload = {
      dob: dayjs(formData?.dob).valueOf(),
      email: formData.email,
      primaryPhone: formData?.primaryPhone?.replace(/[^\d]/g, ''),
      firstName: formData.firstName,
      lastName: formData.lastName,
      licenseNumber: formData?.licenseNumber,
      gender: formData?.gender,
      status: formData?.status === 'active' ? true : false,
      deliveryAddress: {
        address: formData?.address,
        city: formData?.city,
        state: formData?.state,
        zipcode: formData?.zipcode,
        latitude: formData?.lat,
        longitude: formData?.lng,
        apartment: formData?.apartment,

      },
      idImage: formData?.idImage,
      createdBy: formData?.createdBy,
      recId: formData?.recId,
      consumerType: formData?.consumerType,
      memberGroup: formData?.memberGroup,
      verifyMethod: 'admin',

    };

    if (memberEmail) {

      dispatch(updateMember({ email: memberEmail, data: payload })).unwrap()
        .then((res) => {
          showError(res?.message, 'success');
          navigate('/hyperwolf/members');
        }).catch((err) => {
          showError(err?.message, 'error');
        });
    } else {
      dispatch(addMember(payload)).unwrap().then((res) => {
        showError(res?.message, 'success');
        navigate('/hyperwolf/members');
      }).catch((err) => {
        showError(err?.message, 'error');
      });
    }

  };

  const showError = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const isMedicnalOr21Less = useMemo(() => {
    if (formData?.consumerType === 'MedicinalUser') return true;
    if (!formData?.dob) return false;
    return isUnder21(formData?.dob);
  }, [formData?.dob, formData?.consumerType]);

  const googleAddressErrorHandler = (status = false) => {
    addressError.current = status;
    if (status) {
      setErrors({ ...errors, address: 'Address is required' });
    }
  };

  useEffect(() => {
    if (!isEmptyObject(memberDetail)) {
      let payload = {
        ...memberDetail, address: memberDetail?.address?.address, dob: memberDetail?.dob ? dayjs(memberDetail?.dob) : '', status: memberDetail?.status ? 'active' : 'inactive'
      }
      // if (memberDetail?.blockOnlinePayments?.status) {
      //   setIsBlock(true)
      // }
      setFormData({ ...payload, createdBy: user?.name });
      if (memberDetail?.deliveryAddress?.zipcode) {
        setFormData({ ...payload, zipcode: memberDetail?.deliveryAddress?.zipcode, createdBy: user?.name });
      }
    }
  }, [memberDetail]);


  const cancelBanMemberModal = useCallback(() => {
    setFormData({ ...formData, status: memberDetail?.status ? 'active' : 'inactive' });
  }, [formData, memberDetail]);

  useEffect(() => {
    setFormData({ ...formData, createdBy: user?.name });
  }, [user?.name]);

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }} className="add-member-form">
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={4.5}>
          <Typography variant='h4' fontWeight="semibold" color="secondary">{memberEmail ? 'Update' : 'Add'} Member</Typography>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Typography variant='h5' fontWeight="semibold" color="secondary">Status</Typography>
            <Stack direction="row" alignItems="center" gap={.75} lineHeight={1} p={.75} borderRadius={2} bgcolor="#F2F3F7">
              <MDButton
                sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                type='button'
                variant={formData?.status === 'active' ? 'contained' : 'text'}
                color={formData?.status === 'active' ? 'success' : 'dark'}
                size="small"
                onClick={() => {
                  setFormData({ ...formData, status: 'active' });
                }}
              >
                Active
              </MDButton>
              <MDButton
                sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                type='button'
                variant={formData?.status !== 'active' ? 'contained' : 'text'}
                color={formData?.status !== 'active' ? 'error' : 'dark'}
                size="small"
                onClick={() => {
                  if (memberEmail && formData?.status !== 'inactive') {
                    dispatch(openCustomModalDialog({ modal: 'BAN_MEMBER', modalData: { banMemberSubmitHandler, cancelHandler: cancelBanMemberModal, memberStatus: 'inactive' } }));
                    setFormData({ ...formData, status: 'inactive' });
                  } else {
                    setFormData({ ...formData, status: 'inactive' });

                  }
                }}
              >
                Inactive
              </MDButton>
            </Stack>
          </Box>
        </Stack>

        <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Personal Details</Typography>
        <Grid container spacing={2.5} mb={4.5}>
          <Grid item xs={12} md={6} lg={3}>
            <TextField className="form-input" error={errors?.firstName} helperText={errors.firstName} fullWidth label="First Name*" name="firstName" value={formData?.firstName || ''} onChange={handleChange}
              disabled />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField className="form-input" error={errors?.lastName} helperText={errors.lastName} fullWidth label="Last Name*" name="lastName" value={formData?.lastName} onChange={handleChange}
              disabled />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField className="form-input" type='email' error={errors?.email} helperText={errors.email} fullWidth label="Email*" name="email" value={formData.email} onChange={handleChange}
              disabled />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <InputMask
              className="form-input"
              fullWidth
              error={errors?.primaryPhone}
              helperText={errors.primaryPhone}
              label="Primary Phone*"
              // name="primaryPhone"
              value={formData.primaryPhone}
              disabled
              onChange={handleChange}
              mask='(999) 999-9999'
              maskChar=''
            >
              {(inputProps) => <TextField {...inputProps} disableUnderline disabled />}
            </InputMask>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <DatePicker
                sx={{ width: '100%' }}
                label="Date of Birth*"
                error={errors?.dob}
                helperText={errors?.dob}
                name="dob"
                disabled
                value={formData?.dob || null}
                onChange={(value) => setFormData({ ...formData, dob: value })}
                slots={{
                  openPickerIcon: CalendarIcon
                }}
                disableFuture
                slotProps={{
                  openPickerButton: {
                    color: 'secondary',
                  },
                }}
                onError={() => {
                  return Boolean(errors?.dob);
                }}
                format='MM/DD/YYYY'
              />
              {Boolean(errors?.dob) && <FormHelperText error>{errors?.dob}</FormHelperText>}
            </FormControl>
          </Grid >
          <Grid item xs={12} md={6} lg={3}>
            <FormControl error={Boolean(errors?.userType)} fullWidth>
              <InputLabel id="user-type-label">User Type*</InputLabel>
              <Select
                className="form-select"
                labelId="user-type-label"
                id="user-type-select"
                value={formData?.consumerType}
                label="User Type*"
                name='consumerType'
                onChange={handleChange}
                disabled
              >
                <MenuItem value={'MedicinalThirdParty'}>Medicinal User</MenuItem>
                <MenuItem value={'AdultUse'}>Adult Use</MenuItem>
              </Select>
              {errors?.userType && <FormHelperText error>{errors?.userType}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl error={Boolean(errors?.memberGroup)} fullWidth>
              <InputLabel id="option-label">Membership Group*</InputLabel>
              <Select
                className="form-select"

                labelId="option-label"
                id="option-select"
                value={formData?.memberGroup?.name}
                label="Membership Group*"
                onChange={handleChange}
                name='memberGroup'
                disabled
              >
                <MenuItem value={'Delivery'}>Delivery</MenuItem>
                <MenuItem value={'Seniors'}>Seniors</MenuItem>
                <MenuItem value={'State Rec-card'}>State Rec-card</MenuItem>
                <MenuItem value={'Veteran'}>Veteran</MenuItem>
                <MenuItem value={'Walk-In'}>Walk-In</MenuItem>
              </Select>
              {errors?.memberGroup && <FormHelperText error>{errors?.memberGroup}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField disabled className="form-input" fullWidth error={errors?.licenseNumber} helperText={errors.licenseNumber} label="License Number" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl error={errors?.gender} fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select disabled className="form-select" helperText={errors.gender} label="Gender" name="gender" value={formData?.sex} onChange={handleChange}>
                <MenuItem value="1">Male</MenuItem>
                <MenuItem value="0">Female</MenuItem>
                <MenuItem value="2">Other</MenuItem>
              </Select>
              {errors?.gender && <FormHelperText error>{errors?.gender}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              className="form-input created-by"
              type='text'
              error={errors?.createdBy}
              helperText={errors.createdBy}
              fullWidth
              disabled
              label="Created By"
              name="createdBy"
              value={formData.createdBy}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle fontSize='medium' color='secondary' />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Apartment Number</Typography>
        <Grid container spacing={2.5} mb={4.5}>
          <Grid item xs={12} className="address-field">
            <TextField className="form-input" name='memo' value={formData?.apartment || ''}
              disabled
              error={Boolean(errors?.fieldError?.apartment)} helperText={errors?.fieldError?.apartment}
              onChange={(e) => setFormData({ ...formData, apartment: e.target.value })} fullWidth label="Apartment Number" />
          </Grid>
        </Grid>

        <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Address</Typography>
        <Grid container spacing={2.5} mb={4.5}>
          <Grid item xs={12} className="address-field">
            { window?.google && <GoogleSuggestion addressValue={formData?.address || ''} istrue="no" showClearIcon={false} googleAddressErrorHandler={googleAddressErrorHandler} onClearAddress={(address) => setFormData({ ...formData, ...address })} addressChangeHandler={(address) => setFormData({ ...formData, ...address })} />}
            {errors?.address && <FormHelperText error>{errors?.address}</FormHelperText>}
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
          <MDButton
            sx={{ minWidth: '160px' }}
            type='button'
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={isUploading || loading}
            loading={loading}
          >
            {memberEmail ? 'Update' : 'Save'}
          </MDButton>
       
          <MDButton
            sx={{ minWidth: '160px' }}
            type='button'
            variant='outlined'
            color='dark'
            onClick={() => navigate('/hyperwolf/members')}
          >
            Cancel
          </MDButton>
        </Stack>
      </Card>
    </Box>
  );
};

export default AddMemberForm;